import { useCallback } from 'react'
import { useStatusOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import useRequestState from './useRequestState'
import { useBookmarks } from '../components/chat/BookmarksProvider'
import orchestrationApi from '../api/orchestration'
import api from '../api/chat'
import { useChatState } from '../state/ChatState'
import useSearchForChatRoom from './useSearchForChatRoom'
import { Chat } from '../global/chatSettingsConfig'
import useCreateGroupChat from './useCreateGroupChat'
import { useChatOverlayContext } from '../components/chat/ChatOverlayProvider'
import { needToAddBookmark } from '../global/bookmark-helper'

export default function useJoinSessionChatRoom(groupType = 'SESSION') {
  const [t] = useTranslation()
  const { bookmarks, refetchBookmarks } = useBookmarks()
  const [searchForChatRoom] = useSearchForChatRoom()
  const [createGroupChat] = useCreateGroupChat()
  const { connection, user } = useChatState()
  const { setIsOpen, isOpen } = useChatOverlayContext()
  const showStatusOverlay = useStatusOverlay()

  const addBookmarkIfNeeded = useCallback(
    (jid, title) => {
      if (needToAddBookmark({ bookmarks, jid })) {
        orchestrationApi.bookmarks.addBookmark({ jid, name: title }).then(refetchBookmarks)
      }
    },
    [bookmarks, refetchBookmarks]
  )

  const joinSessionChatRoom = useCallback(
    async (title, description) => {
      const existingBookmark = bookmarks.find(({ name }) => name === title)
      if (!isOpen) {
        setIsOpen(true)
      } else {
        // If the window is already open, reset the router history before joining the session chat.
        window.chatHistory.push(`/connect/direct`)
      }

      if (existingBookmark) {
        window.chatHistory.push(`/chat-room/group/${existingBookmark.jid}/${user.authzid}`)
      } else {
        showStatusOverlay({
          message: t('chat.joining', 'Joining chat room...'),
          transient: true,
        })

        const existingChatRoom = await searchForChatRoom({ searchStr: title, type: groupType, matchExactly: true })

        if (existingChatRoom) {
          const groupJid = `${existingChatRoom.jid}@${Chat.Settings.MUC_DOMAIN}`
          title = existingChatRoom.title

          addBookmarkIfNeeded(groupJid, title)
          await api.groups.joinGroupChat({ chatRoom: groupJid, fromJid: user.authzid, connection })

          // Then navigate to it
          window.chatHistory.push(`/chat-room/group/${groupJid}/${user.authzid}`)
        } else {
          const [uniqueId, chatRoomDetails] = await createGroupChat({ friendlyName: title, groupType, description })
          title = chatRoomDetails.title

          addBookmarkIfNeeded(uniqueId, title)

          window.chatHistory.push(`/chat-room/group/${uniqueId}/${user.authzid}`)
        }

        showStatusOverlay({
          message: t('chat.joined-chat-room', 'Joined chat: "{chatRoom}"', {
            chatRoom: title,
          }),
          type: 'success',
          transient: true,
        })
      }
    },
    [
      addBookmarkIfNeeded,
      bookmarks,
      connection,
      createGroupChat,
      groupType,
      isOpen,
      searchForChatRoom,
      setIsOpen,
      showStatusOverlay,
      t,
      user.authzid,
    ]
  )

  return useRequestState(joinSessionChatRoom)
}
